import React from 'react';
import { styled } from '@mui/material';
import { Link } from 'react-router-dom';
import { Colors } from 'library';

type RLinkSize = 'sm' | 'md' | 'lg';
type RLinkWeight = 'medium' | 'semibold';

type RLinkBaseProps = {
    children: React.ReactNode;
    size?: RLinkSize;
    weight?: RLinkWeight;
    underline?: boolean;
    color?: string;
    icon?: React.ReactNode;
    className?: string;
};

type RLinkInternalProps = RLinkBaseProps & { to: string; state?: unknown; href?: never; onClick?: never };
type RLinkExternalProps = RLinkBaseProps & { href: string; to?: never; onClick?: never };
type RLinkButtonProps = RLinkBaseProps & { onClick: (e: React.MouseEvent) => void; to?: never; href?: never };
type RLinkProps = RLinkInternalProps | RLinkExternalProps | RLinkButtonProps;

const SIZE_PX: Record<RLinkSize, number> = { sm: 13, md: 14, lg: 18 };
const WEIGHT_VALUE: Record<RLinkWeight, number> = { medium: 500, semibold: 600 };

export function RLink({
    children,
    size = 'md',
    weight = 'semibold',
    underline = true,
    color,
    icon,
    className,
    ...rest
}: RLinkProps) {
    const content = (
        <>
            {icon}
            {children}
        </>
    );

    if ('href' in rest && rest.href) {
        return (
            <ExternalLink
                href={rest.href}
                $size={size}
                $weight={weight}
                $underline={underline}
                $color={color}
                target="_blank"
                rel="noopener noreferrer"
                className={className}
            >
                {content}
            </ExternalLink>
        );
    }
    if ('onClick' in rest && rest.onClick) {
        return (
            <ButtonLink
                type="button"
                onClick={rest.onClick}
                $size={size}
                $weight={weight}
                $underline={underline}
                $color={color}
                className={className}
            >
                {content}
            </ButtonLink>
        );
    }
    const internalProps = rest as RLinkInternalProps;
    const linkTo = internalProps.state ? { pathname: internalProps.to, state: internalProps.state } : internalProps.to;
    return (
        <InternalLink
            to={linkTo}
            $size={size}
            $weight={weight}
            $underline={underline}
            $color={color}
            className={className}
        >
            {content}
        </InternalLink>
    );
}

type LinkStyleProps = { $size: RLinkSize; $weight: RLinkWeight; $underline: boolean; $color?: string };

const linkStyles = ({ $size, $weight, $underline, $color }: LinkStyleProps) => {
    const resolvedColor = $color ?? Colors.darkNavy;
    return {
        display: 'inline-flex' as const,
        alignItems: 'center' as const,
        gap: 4,
        fontSize: SIZE_PX[$size],
        fontWeight: WEIGHT_VALUE[$weight],
        color: resolvedColor,
        textDecoration: $underline ? ('underline' as const) : ('none' as const),
        '&:hover': { color: resolvedColor, opacity: 0.7 },
    };
};

const shouldForwardLinkProp = (prop: string) => !['$size', '$weight', '$underline', '$color'].includes(prop);

const InternalLink = styled(Link, {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles);

const ExternalLink = styled('a', {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles);

const ButtonLink = styled('button', {
    shouldForwardProp: (prop) => shouldForwardLinkProp(prop as string),
})<LinkStyleProps>(linkStyles, {
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:focus, &:focus-visible': { outline: 'none' },
});
